import React from 'react';

interface Props {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Pagination: React.FC<Props> = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  // Build page window: always show first, last, current ±2
  const pages: (number | '...')[] = [];
  const delta = 2;
  const range: number[] = [];
  for (
    let i = Math.max(2, currentPage - delta);
    i <= Math.min(totalPages - 1, currentPage + delta);
    i++
  ) {
    range.push(i);
  }
  pages.push(1);
  if (range[0] > 2) pages.push('...');
  range.forEach((p) => pages.push(p));
  if (range[range.length - 1] < totalPages - 1) pages.push('...');
  if (totalPages > 1) pages.push(totalPages);

  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
      {/* Info */}
      <div className="text-muted small">
        Showing <strong>{start}</strong>–<strong>{end}</strong> of{' '}
        <strong>{totalItems}</strong> records
      </div>

      <div className="d-flex align-items-center gap-3">
        {/* Page size selector */}
        {onPageSizeChange && (
          <div className="d-flex align-items-center gap-2">
            <label className="text-muted small mb-0">Rows:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 70 }}
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}

        {/* Page buttons */}
        <nav aria-label="Table pagination">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &laquo;
              </button>
            </li>
            {pages.map((p, i) =>
              p === '...' ? (
                <li key={`ellipsis-${i}`} className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              ) : (
                <li
                  key={p}
                  className={`page-item ${p === currentPage ? 'active' : ''}`}
                >
                  <button
                    className="page-link"
                    onClick={() => onPageChange(p as number)}
                  >
                    {p}
                  </button>
                </li>
              )
            )}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &raquo;
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
